import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, map, of, tap } from "rxjs";
import { environment } from "../../../environments/environment";
import { Member } from "../models/member";
import { UpdateMember } from "../models/update-member";
import { MemberReferenceVm } from "../models/member-reference-vm";
import { MemberUpdateCommand } from "../models/member-update-command.model";
import { PolicyChangeLog } from "../models/policy-change-log.model";
import { USER_SESSION } from "../constants/localstorage.constants";

@Injectable({
  providedIn: "root",
})
export class MembersService {
  private readonly url = "members";
  private readonly baseUrl: string;
  constructor(private readonly http: HttpClient) {
    this.baseUrl = environment.apiConfig.uri + this.url;
  }

  getMemberInformations(): Observable<Member> {
    const cachedMember = sessionStorage.getItem(USER_SESSION);
    if (cachedMember) {
      return of(JSON.parse(cachedMember) as Member);
    }

    return this.http
      .get<Member>(this.baseUrl + "/me")
      .pipe(tap(member => sessionStorage.setItem(USER_SESSION, JSON.stringify(member))));
  }

  isEnrolled(email: string): Observable<boolean> {
    return this.http.get<boolean>(this.baseUrl + "/" + email + "/enrollmentStatus");
  }

  emailExists(email: string): Observable<boolean> {
    const url = environment.apiConfig.uri + this.url + "/" + email;
    return this.http.head(url, { observe: "response" }).pipe(
      map(response => {
        if (response.status === 200) {
          return true;
        } else if (response.status === 204) {
          return false;
        } else {
          throw new Error(`Unexpected status code: ${response.status}`);
        }
      }),
      catchError(error => {
        console.error("Error:", error);
        throw new Error(`Unexpected error: ${error}`);
      })
    );
  }

  updateMemberContact(command: UpdateMember): Observable<void> {
    return this.http.patch<void>(this.baseUrl, command);
  }

  updateEnrollmentStatus(email: string, providedPassword: string, isEnrolled: boolean): Observable<void> {
    return this.http.patch<void>(this.baseUrl + "/enrollmentStatus", {
      email: email,
      providedPassword: providedPassword,
      isEnrolled: isEnrolled,
    });
  }

  sendPasswordResetEmail(email: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/passwordReset`, { email });
  }

  getMemberReferences(memberId: string): Observable<MemberReferenceVm[]> {
    return this.http.get<MemberReferenceVm[]>(`${this.baseUrl}/${memberId}/references/me`);
  }

  computeMemberUpdate(memberUpdateCommand: MemberUpdateCommand): Observable<PolicyChangeLog> {
    return this.http.post<PolicyChangeLog>(this.baseUrl + "/changes/me", memberUpdateCommand);
  }

  updateMemberChange(memberUpdateCommand: MemberUpdateCommand): Observable<void> {
    return this.http.patch<void>(this.baseUrl + "/me", memberUpdateCommand);
  }
}
